import { Activity, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Building2,
  BriefcaseBusiness,
  Check,
  ListChecks,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Cpu,
  Home,
  Inbox,
  LogOut,
  MessageSquare,
  NotebookPen,
  Palette,
  Plus,
  Search,
  Send,
  Settings as SettingsIcon,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { appRoutes, getRouteById, matchRoute, type AppRoute } from '@/pages/page-registry';
import { useOutreachCounts } from '@/modules/outreach-v2';
import { jobApi, type JobPosting } from '@/modules/job/services/job.api';
import { listSearches as listSavedSearches, type SearchDoc } from '@/pages/search/api';
import { useFullPagerStore } from '@/modules/shared/store/useFullPagerStore';
import { onSidebarPeekRequest } from '@/lib/sidebarState';
import { hasCryptoLocalSearch, hasOutreachPreset, isDefaultOrg, isSearchOnlyOrg } from '@/lib/orgGates';
import { CommandPalette } from './CommandPalette';
import { type OpenTab, useTabs } from './useTabs';
import { TabContext } from './TabContext';
import { FullPagerPortal } from '@/modules/shared/components/FullPager';
import { NotFoundPage } from '@/pages/NotFoundPage';

const COLLAPSE_KEY = 'v2-sidebar-collapsed';
const MULTI_TAB_KEY = 'ats-spa.multi-tab-enabled';
const KEEP_ALIVE_KEY = 'ats-spa.keep-alive-enabled';
const TABBAR_COLLAPSED_KEY = 'ats-spa.tabbar-collapsed';
const DS_GROUP_KEY = 'v2-sidebar-ds-open';
const DEV_GROUP_KEY = 'v2-sidebar-dev-open';
const DS_ROUTE = '/v2/design-system';
const DEFAULT_STORY_KEY = 'typography';
const HOME_PATH = '/';
const HOME_TAB_ID = '__home__';

type NavItem = {
  routeId: string;
  label: string;
  matchPath: string;
  /**
   * Extra path prefixes that also light this item up. Used by the Outreach hub
   * item, which lands on /v2/inbox but stays active across every hub surface
   * (Analytics, /v2/outreach/*). Falls back to [matchPath].
   */
  matchPaths?: string[];
  href?: string;
  icon?: LucideIcon;
  /** Optional org-preset gate (e.g. crypto-local-search); item is hidden when it returns false. */
  requirePreset?: (organization: Parameters<typeof hasCryptoLocalSearch>[0]) => boolean;
};

const PRIMARY_NAV_ITEMS: NavItem[] = [
  {
    // Mirrors Claude's "New chat" affordance — clicking lands on Home,
    // where the centered composer creates the chat and hands off the
    // first message to /v2/chats/:id.
    routeId: 'home',
    label: 'New session',
    matchPath: '/__never_active__',
    href: '/',
    icon: Plus,
  },
  { routeId: 'chats-list', label: 'Sessions', matchPath: '/v2/chats', href: '/v2/chats', icon: MessageSquare },
  { routeId: 'jobs', label: 'Job roles', matchPath: '/v2/jobs', href: '/v2/jobs', icon: BriefcaseBusiness },
  // YC demo: Plan 03 — Tasks inbox surfaces report-review decisions waiting on the user.
  { routeId: 'tasks', label: 'Tasks', matchPath: '/tasks', href: '/tasks', icon: ListChecks },
  { routeId: 'agent-chats-list', label: 'Searches', matchPath: '/searches', href: '/searches', icon: Search },
  // Outreach hub — single front door to the cross-job CRM. Lands on the Inbox;
  // permission-gated via the `inbox` route's outreach.view requirement.
  {
    routeId: 'inbox',
    label: 'Outreach',
    matchPath: '/v2/inbox',
    matchPaths: ['/v2/inbox', '/v2/outreach'],
    href: '/v2/inbox',
    icon: Send,
  },
  { routeId: 'search', label: 'Search', matchPath: '/search', href: '/search', icon: Search },
  {
    routeId: 'talent-pool-search',
    label: 'Talent Pool',
    matchPath: '/talent-pool',
    href: '/talent-pool',
    icon: Users,
    requirePreset: hasCryptoLocalSearch,
  },
  { routeId: 'notetakers', label: 'Notetakers', matchPath: '/notetakers', href: '/notetakers', icon: NotebookPen },
];

const DEV_NAV_ITEMS: NavItem[] = [
  { routeId: 'agent-dashboard', label: 'Agent Dashboard', matchPath: '/v2/agent-dashboard' },
  { routeId: 'jobs-v2', label: 'Jobs v2 Reference', matchPath: '/v2/jobs-v2' },
  { routeId: 'calendar', label: 'Calendar', matchPath: '/v2/calendar' },
  { routeId: 'people', label: 'People', matchPath: '/v2/people' },
  { routeId: 'custom-fields', label: 'Custom Fields', matchPath: '/v2/people/custom-fields' },
  { routeId: 'playground-kanban', label: 'Kanban (Playground)', matchPath: '/v2/playground/kanban' },
  { routeId: 'playground-report', label: 'Candidate Report (Playground)', matchPath: '/v2/playground/candidate-report' },
  { routeId: 'playground-kanban-v2', label: 'Kanban v2 (HR)', matchPath: '/v2/playground/kanban-v2' },
  { routeId: 'playground-report-v2', label: 'Report v2 (HR)', matchPath: '/v2/playground/report-v2' },
  { routeId: 'playground-report-v3', label: 'Report v3 (Hero)', matchPath: '/v2/playground/report-v3' },
  { routeId: 'playground-kanban-v3', label: 'Kanban v3 (Overlay)', matchPath: '/v2/playground/kanban-v3' },
  { routeId: 'permission-test', label: 'Permission Test', matchPath: '/v2/permission-test' },
];

const BOTTOM_NAV_ITEMS: NavItem[] = [
  { routeId: 'brain', label: 'Brain', matchPath: '/brain', icon: Brain },
];

// `/v2/settings` is here because every `/v2/settings/*` route is a redirect to a
// `/settings/*` fullscreen page (see REDIRECT_MAP in page-registry). Without it,
// landing on `/v2/settings/roles` (e.g. after create-org reload, or any sidebar
// link) briefly registers a non-fullscreen location, which the Back button then
// navigates to — kicking off the redirect again and stranding the user.
const FULLSCREEN_PATH_PREFIXES = ['/settings', '/brain', '/v2/settings'];

function isFullscreenPath(pathname: string) {
  return FULLSCREEN_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Routes whose page header renders its own in-flow expand-sidebar icon
 * (components/SidebarExpandButton). The global floating peek-trigger is
 * hidden on these via `data-route='self-toggle'` + app.css, because their
 * full-width headers put content (back arrow, title, toolbar) directly
 * under where the floating trigger sits.
 */
function routeRendersOwnSidebarToggle(pathname: string) {
  // Search chat detail (the original reference implementation).
  if (pathname.startsWith('/search/') && pathname !== '/search/new') return true;
  // Search v2 chat detail (same page; suppress the global toggle so the
  // page's own SidebarExpandButton isn't duplicated).
  if (pathname.startsWith('/search-v2/') && pathname !== '/search-v2/new') return true;
  // Search v3 chat detail (same agentType-agnostic page as v1/v2).
  if (pathname.startsWith('/search-v3/') && pathname !== '/search-v3/new') return true;
  // Search v3 index uses a custom full-width glass header; render the
  // sidebar affordance in-flow so the floating trigger does not sit on it.
  if (pathname === '/searches-v3' || pathname === '/searches-v3/') return true;
  if (
    pathname === '/staff/search-v3-autosourcing' ||
    pathname === '/staff/search-v3-autosourcing/'
  ) return true;
  // Jobs list + job detail (/v2/jobs/:id — one extra segment, excluding
  // static children like /v2/jobs/new and /v2/jobs/new-role/* which are
  // centered layouts that keep the global trigger).
  if (pathname === '/v2/jobs') return true;
  if (/^\/v2\/jobs\/[^/]+$/.test(pathname) && pathname !== '/v2/jobs/new') return true;
  // Chats list + chat detail.
  if (pathname === '/v2/chats' || pathname.startsWith('/v2/chats/')) return true;
  // Default-org Sessions + Jobs lists render their own in-header expand button.
  if (pathname === '/sessions') return true;
  if (pathname === '/jobs') return true;
  if (pathname === '/v2/calendar') return true;
  if (pathname === '/notetakers') return true;
  // Outreach hub (Campaigns/Inbox/Analytics/Settings) renders its expand icon
  // in the OutreachHubLayout tab strip; the standalone campaign builder +
  // sequence editor and the in-job outreach page render theirs beside the
  // back arrow. All have top-left header content the floating trigger collides
  // with, so suppress the global one on every outreach surface.
  if (pathname.startsWith('/v2/outreach/')) return true;
  if (pathname === '/v2/inbox') return true;
  if (/^\/v2\/jobs\/[^/]+\/outreach$/.test(pathname)) return true;
  // Talent Pool renders its own SidebarExpandButton in its top bar, so suppress
  // the global floating peek-trigger (it would collide with the top-bar nav).
  if (pathname === '/talent-pool' || pathname.startsWith('/talent-pool/')) return true;
  return false;
}

/**
 * The last non-fullscreen URL the user was on. Updated whenever AppShell
 * sees a non-fullscreen pathname, read by FullscreenTopBar's Back button so
 * exiting Settings/Brain always returns to the regular app in one click — no
 * matter how deep you've navigated inside the fullscreen rail.
 */
let lastNonFullscreenLocation = '/';

const DESIGN_SYSTEM_STORIES = [
  { key: 'typography', label: 'Typography' },
  { key: 'buttons', label: 'Buttons' },
  { key: 'inputs', label: 'Inputs' },
  { key: 'cards', label: 'Cards' },
  { key: 'modals', label: 'Modals' },
  { key: 'tables', label: 'Tables' },
  { key: 'chat', label: 'Chat' },
  { key: 'navigation', label: 'Navigation' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'settings', label: 'Settings' },
  { key: 'kanban', label: 'Kanban' },
  { key: 'agentic', label: 'Agentic Chat' },
];

function materializePath(route: AppRoute) {
  return route.path.includes(':') ? route.path.replace(/:[^/]+/g, 'demo') : route.path;
}

function getOrgLogo(org: { _id: string; logoKey?: string } | null) {
  return org?.logoKey || `https://api.dicebear.com/9.x/glass/svg?seed=${org?._id || 'default'}`;
}

function getUserAvatar(email?: string) {
  return `https://api.dicebear.com/9.x/thumbs/svg?seed=${email || 'default'}`;
}

function getUserName(user: ReturnType<typeof useAuth>['user']) {
  return [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';
}

function readMultiTabEnabled() {
  try {
    return window.localStorage.getItem(MULTI_TAB_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeMultiTabEnabled(enabled: boolean) {
  try {
    window.localStorage.setItem(MULTI_TAB_KEY, String(enabled));
  } catch {
    // Ignore storage failures; the in-memory toggle should still work.
  }
}

// Kill switch for the multi-tab keep-alive engine. Default ON. When OFF, the
// shell falls back to a single <Outlet /> render and tabs remount on switch.
// Toggle via DevTools: localStorage.setItem('ats-spa.keep-alive-enabled', 'false').
function readKeepAliveEnabled() {
  try {
    return window.localStorage.getItem(KEEP_ALIVE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function readTabBarCollapsed() {
  try {
    return window.localStorage.getItem(TABBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeTabBarCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(TABBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Ignore storage failures.
  }
}

function useFloatingRect(open: boolean, onDismiss: () => void) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!open) {
      setRect(null);
      return undefined;
    }

    const update = () => setRect(triggerRef.current?.getBoundingClientRect() || null);
    update();

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      onDismiss();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onDismiss();
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onDismiss, open]);

  return { triggerRef, popoverRef, rect };
}

function PanelToggleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M5.474 19h13.052c.834 0 1.455-.202 1.863-.605.407-.403.611-1.008.611-1.815V7.42c0-.807-.204-1.412-.611-1.815C19.98 5.202 19.36 5 18.526 5H5.474c-.821 0-1.439.202-1.853.605C3.207 6.008 3 6.613 3 7.42v9.16c0 .807.207 1.412.621 1.815.414.403 1.032.605 1.853.605Zm.02-1.392c-.35 0-.62-.091-.81-.274-.191-.182-.287-.456-.287-.821V7.487c0-.359.096-.63.287-.816.19-.186.46-.279.81-.279h13.012c.35 0 .62.093.81.279.191.185.287.457.287.816v9.026c0 .365-.096.639-.287.821-.19.183-.46.274-.81.274H5.494Zm3.377.268h1.368V6.152H8.87v11.724ZM7.415 9.178a.45.45 0 0 0 .33-.149c.097-.1.146-.21.146-.331a.433.433 0 0 0-.146-.322.465.465 0 0 0-.33-.14H5.882a.465.465 0 0 0-.33.14.433.433 0 0 0-.146.322c0 .121.049.232.146.331a.45.45 0 0 0 .33.149h1.533Zm0 1.92a.457.457 0 0 0 .33-.144.459.459 0 0 0 .146-.336.433.433 0 0 0-.146-.321.465.465 0 0 0-.33-.14H5.882a.465.465 0 0 0-.33.14.433.433 0 0 0-.146.321c0 .128.049.24.146.336a.457.457 0 0 0 .33.144h1.533Zm0 1.92a.465.465 0 0 0 .33-.138.443.443 0 0 0 .146-.332.433.433 0 0 0-.146-.321.465.465 0 0 0-.33-.14H5.882a.465.465 0 0 0-.33.14.433.433 0 0 0-.146.321c0 .128.049.239.146.332.097.092.207.139.33.139h1.533Z" fill="currentColor" />
    </svg>
  );
}

function SidebarNavItem({
  item,
  route,
  collapsed,
  active,
  badge,
}: {
  item: NavItem;
  route: AppRoute;
  collapsed: boolean;
  active: boolean;
  badge?: number;
}) {
  const navigate = useNavigate();
  const Icon = item.icon || route.icon;
  const showBadge = typeof badge === 'number' && badge > 0;

  return (
    <div className={active ? 'ats-nav-row active' : 'ats-nav-row'}>
      <button
        type="button"
        className="ats-nav-link"
        title={collapsed ? item.label : undefined}
        onClick={() => navigate(item.href || materializePath(route))}
      >
        <span className="ats-nav-icon">
          <Icon size={16} strokeWidth={1.75} />
        </span>
        <span className="ats-nav-label">{item.label}</span>
        {showBadge ? (
          <span
            aria-label={`${badge} need attention`}
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              fontSize: 10,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              background: 'var(--accent)',
              color: '#fff',
            }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>
    </div>
  );
}

type UserMenuVariant = 'sidebar' | 'topbar';

function UserMenu({ collapsed, variant = 'sidebar' }: { collapsed?: boolean; variant?: UserMenuVariant }) {
  const {
    user,
    memberships,
    logout,
    organization,
    organizations,
    workspaces,
    currentWorkspaceId,
    switchWorkspace,
    switchOrg,
    createWorkspace,
    hasPermission,
  } = useAuth();
  // Backend gates: createOrg is staff-only, createWorkspace requires
  // workspace.edit. Hide the affordances entirely for users who lack them
  // so Members don't see admin-only entry points in the user menu.
  const canCreateWorkspace = hasPermission('workspace.edit');
  const canCreateOrganization = Boolean(user?.isStaff);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState<'workspaces' | 'organizations' | null>(null);
  const submenuTimerRef = useRef<number | null>(null);
  const workspacesRowRef = useRef<HTMLButtonElement>(null);
  const organizationsRowRef = useRef<HTMLButtonElement>(null);
  const [submenuTop, setSubmenuTop] = useState<number>(0);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const { triggerRef, popoverRef, rect } = useFloatingRect(open, () => setOpen(false));
  const topRole = memberships[0]?.roleName || 'No role';
  const userName = getUserName(user);
  const currentWorkspace = workspaces.find((workspace) => workspace._id === currentWorkspaceId);
  const otherOrgs = organizations.filter((org) => org._id !== organization?._id);

  function clearSubmenuTimer() {
    if (submenuTimerRef.current !== null) {
      window.clearTimeout(submenuTimerRef.current);
      submenuTimerRef.current = null;
    }
  }

  function openSubmenu(target: 'workspaces' | 'organizations') {
    clearSubmenuTimer();
    const ref = target === 'workspaces' ? workspacesRowRef : organizationsRowRef;
    if (ref.current && popoverRef.current) {
      const rowTop = ref.current.getBoundingClientRect().top;
      const popoverTop = popoverRef.current.getBoundingClientRect().top;
      setSubmenuTop(Math.max(0, rowTop - popoverTop));
    }
    setSubmenu(target);
  }

  function scheduleCloseSubmenu() {
    clearSubmenuTimer();
    submenuTimerRef.current = window.setTimeout(() => {
      setSubmenu(null);
      submenuTimerRef.current = null;
    }, 160);
  }

  useEffect(() => {
    if (!open) {
      setSubmenu(null);
      setCreatingWorkspace(false);
      setWorkspaceName('');
      clearSubmenuTimer();
    }
  }, [open]);

  useEffect(() => () => clearSubmenuTimer(), []);

  // Clamp the submenu so it never overflows the viewport. Runs after layout
  // so we can measure the rendered submenu's height and shift it up if its
  // bottom would fall below the screen (or down if its top would clip above).
  useLayoutEffect(() => {
    if (!submenu || !popoverRef.current) return;
    const submenuEl = popoverRef.current.querySelector('.ats-user-submenu') as HTMLElement | null;
    if (!submenuEl) return;
    const submenuRect = submenuEl.getBoundingClientRect();
    const margin = 8;
    const viewportH = window.innerHeight;
    if (submenuRect.bottom > viewportH - margin) {
      const overflow = submenuRect.bottom - (viewportH - margin);
      setSubmenuTop((prev) => Math.max(0, prev - overflow));
    } else if (submenuRect.top < margin) {
      const underflow = margin - submenuRect.top;
      setSubmenuTop((prev) => prev + underflow);
    }
  }, [submenu, submenuTop, popoverRef]);

  async function handleSwitchWorkspace(workspaceId: string) {
    await switchWorkspace(workspaceId);
    setOpen(false);
  }

  async function handleSwitchOrg(organizationId: string) {
    await switchOrg(organizationId);
    setOpen(false);
  }

  async function submitWorkspace() {
    if (!workspaceName.trim()) return;
    await createWorkspace(workspaceName.trim());
    setWorkspaceName('');
    setCreatingWorkspace(false);
  }

  async function handleLogout() {
    setOpen(false);
    const idpLogoutUrl = await logout();
    if (idpLogoutUrl) {
      window.location.href = idpLogoutUrl;
    } else {
      navigate('/login');
    }
  }

  const positionStyle: React.CSSProperties =
    rect && variant === 'sidebar'
      ? { left: rect.right + 8, bottom: window.innerHeight - rect.bottom }
      : rect
        ? { right: window.innerWidth - rect.right, top: rect.bottom + 6 }
        : {};

  const triggerClassName = variant === 'sidebar' ? 'ats-user-trigger' : 'ats-user-trigger compact';

  return (
    <div className={variant === 'sidebar' ? 'ats-user' : 'ats-user inline'}>
      <button ref={triggerRef} type="button" className={triggerClassName} onClick={() => setOpen((value) => !value)}>
        <img src={getUserAvatar(user?.email)} alt="" className="ats-user-avatar" />
        {variant === 'sidebar' ? (
          <>
            <span className="ats-user-copy">
              <strong>{userName}</strong>
              <small>{topRole}</small>
            </span>
            <ChevronDown size={13} strokeWidth={1.75} className="ats-user-chevron" />
          </>
        ) : null}
      </button>
      {open && rect
        ? createPortal(
            <div
              ref={popoverRef}
              className="ats-user-popover"
              style={positionStyle}
              onMouseLeave={scheduleCloseSubmenu}
            >
              <div className="ats-user-popover-header">
                <p>{userName}</p>
                <small>{user?.email}</small>
                <span>{topRole}</span>
              </div>

              <div className="ats-account-workspace-summary">
                <img src={getOrgLogo(organization)} alt="" />
                <span>
                  <strong>{currentWorkspace?.name || 'No Workspace'}</strong>
                  <small>{organization?.name || 'No Organization'}</small>
                </span>
              </div>

              <div className="ats-user-popover-actions">
                <button
                  ref={workspacesRowRef}
                  type="button"
                  className="ats-popover-row submenu-row"
                  onMouseEnter={() => openSubmenu('workspaces')}
                  onFocus={() => openSubmenu('workspaces')}
                  onClick={() => (submenu === 'workspaces' ? setSubmenu(null) : openSubmenu('workspaces'))}
                >
                  <span className="ats-submenu-row-content">
                    <Building2 size={14} strokeWidth={1.75} />
                    <span>Workspaces</span>
                    <small>{currentWorkspace?.name}</small>
                  </span>
                  <ChevronRight size={14} strokeWidth={1.75} />
                </button>
                {otherOrgs.length > 0 ? (
                  <button
                    ref={organizationsRowRef}
                    type="button"
                    className="ats-popover-row submenu-row"
                    onMouseEnter={() => openSubmenu('organizations')}
                    onFocus={() => openSubmenu('organizations')}
                    onClick={() => (submenu === 'organizations' ? setSubmenu(null) : openSubmenu('organizations'))}
                  >
                    <span className="ats-submenu-row-content">
                      <Building2 size={14} strokeWidth={1.75} />
                      <span>Organizations</span>
                      <small>{organization?.name}</small>
                    </span>
                    <ChevronRight size={14} strokeWidth={1.75} />
                  </button>
                ) : null}
              </div>

              <div className="ats-user-popover-actions">
                <button
                  type="button"
                  onMouseEnter={() => setSubmenu(null)}
                  onClick={() => {
                    setOpen(false);
                    navigate('/settings');
                  }}
                >
                  <SettingsIcon size={14} strokeWidth={1.75} /> Settings
                </button>
                {canCreateOrganization ? (
                  <button
                    type="button"
                    onMouseEnter={() => setSubmenu(null)}
                    onClick={() => {
                      setOpen(false);
                      navigate('/v2/create-org');
                    }}
                  >
                    <Plus size={14} strokeWidth={1.75} /> Create Organization
                  </button>
                ) : null}
              </div>
              <div className="ats-user-popover-danger" onMouseEnter={() => setSubmenu(null)}>
                <button type="button" onClick={handleLogout}>
                  <LogOut size={14} strokeWidth={1.75} /> Sign out
                </button>
              </div>

              {submenu === 'workspaces' ? (
                <div
                  className="ats-user-submenu"
                  style={{ top: submenuTop }}
                  onMouseEnter={() => clearSubmenuTimer()}
                  onMouseLeave={scheduleCloseSubmenu}
                >
                  <div className="ats-popover-section-title compact">Workspaces</div>
                  <div className="ats-popover-list ats-account-workspace-list">
                    {workspaces.map((workspace) => {
                      const isCurrent = workspace._id === currentWorkspaceId;
                      return (
                        <button
                          key={workspace._id}
                          type="button"
                          className={isCurrent ? 'ats-popover-row workspace active' : 'ats-popover-row workspace'}
                          onClick={() => void handleSwitchWorkspace(workspace._id)}
                        >
                          <span>
                            <strong>{workspace.name}</strong>
                            <small>{workspace.organizationId === organization?._id ? organization?.name : 'Workspace'}</small>
                          </span>
                          {isCurrent ? <Check size={14} strokeWidth={2} /> : null}
                        </button>
                      );
                    })}
                  </div>

                  {canCreateWorkspace ? (
                    creatingWorkspace ? (
                      <div className="ats-workspace-create account">
                        <div className="ats-workspace-create-row">
                          <input
                            value={workspaceName}
                            onChange={(event) => setWorkspaceName(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') void submitWorkspace();
                              if (event.key === 'Escape') {
                                setCreatingWorkspace(false);
                                setWorkspaceName('');
                              }
                            }}
                            autoFocus
                            placeholder="Workspace name"
                          />
                          <button type="button" className="ats-create-confirm" onClick={() => void submitWorkspace()}>
                            Add
                          </button>
                          <button
                            type="button"
                            className="ats-create-cancel"
                            onClick={() => {
                              setCreatingWorkspace(false);
                              setWorkspaceName('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="ats-popover-action inline" onClick={() => setCreatingWorkspace(true)}>
                        <Plus size={14} strokeWidth={1.75} /> New workspace
                      </button>
                    )
                  ) : null}
                </div>
              ) : null}

              {submenu === 'organizations' && otherOrgs.length > 0 ? (
                <div
                  className="ats-user-submenu"
                  style={{ top: submenuTop }}
                  onMouseEnter={() => clearSubmenuTimer()}
                  onMouseLeave={scheduleCloseSubmenu}
                >
                  <div className="ats-popover-section-title compact">Organizations</div>
                  <div className="ats-popover-list compact">
                    {organizations.map((org) => {
                      const isCurrent = org._id === organization?._id;
                      return (
                        <button
                          key={org._id}
                          type="button"
                          className={isCurrent ? 'ats-popover-row org active' : 'ats-popover-row org'}
                          onClick={() => void handleSwitchOrg(org._id)}
                        >
                          <img src={getOrgLogo(org)} alt="" />
                          <span>{org.name}</span>
                          {isCurrent ? <Check size={14} strokeWidth={2} /> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function DesignSystemGroup({ collapsed }: { collapsed: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const onDsRoute = location.pathname.startsWith(DS_ROUTE);
  const activeStoryKey = onDsRoute ? searchParams.get('story') || DEFAULT_STORY_KEY : null;
  const [open, setOpen] = useState(onDsRoute);

  useEffect(() => {
    const stored = window.localStorage.getItem(DS_GROUP_KEY);
    if (stored !== null) setOpen(stored === 'true');
  }, []);

  useEffect(() => {
    if (onDsRoute) setOpen(true);
  }, [onDsRoute]);

  function toggle() {
    setOpen((value) => {
      const next = !value;
      window.localStorage.setItem(DS_GROUP_KEY, String(next));
      return next;
    });
  }

  if (collapsed) {
    return (
      <button
        type="button"
        className="ats-design-system-collapsed"
        title="Design System"
        onClick={() => navigate(`${DS_ROUTE}?story=${DEFAULT_STORY_KEY}`)}
      >
        <span className={onDsRoute ? 'active' : ''}>
          <Palette size={16} strokeWidth={1.75} />
        </span>
      </button>
    );
  }

  return (
    <div className="ats-design-system-group">
      <button
        type="button"
        className={onDsRoute ? 'ats-design-system-header active' : 'ats-design-system-header'}
        onClick={toggle}
      >
        <ChevronRight size={12} strokeWidth={2} className={open ? 'open' : ''} />
        <span>{'Design System'}</span>
        {onDsRoute ? <strong>•</strong> : null}
      </button>
      <div className={open ? 'ats-design-system-stories open' : 'ats-design-system-stories'}>
        {DESIGN_SYSTEM_STORIES.map((story) => {
          const active = onDsRoute && activeStoryKey === story.key;
          return (
            <button
              key={story.key}
              type="button"
              className={active ? 'active' : ''}
              onClick={() => navigate(`${DS_ROUTE}?story=${story.key}`)}
            >
              {story.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RecentJobs({ collapsed, enabled = true }: { collapsed: boolean; enabled?: boolean }) {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [recentJobs, setRecentJobs] = useState<JobPosting[]>([]);
  const canViewJobs = permissions.includes('job.view');

  useEffect(() => {
    if (!enabled || !canViewJobs) {
      setRecentJobs([]);
      return undefined;
    }

    let cancelled = false;
    jobApi.listJobs({ limit: 6, sort: '-updatedAt', notStatus: 'archived' })
      .then(({ data }) => {
        if (!cancelled) setRecentJobs(data.results || []);
      })
      .catch(() => {
        if (!cancelled) setRecentJobs([]);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, canViewJobs]);

  if (recentJobs.length === 0) return null;

  return (
    <div className={collapsed ? 'ats-recent-jobs collapsed-hidden' : 'ats-recent-jobs'}>
      <p>Recent</p>
      {recentJobs.slice(0, 4).map((job) => (
        <button key={job._id} type="button" onClick={() => navigate(`/v2/jobs/${job._id}`)}>
          {job.title || 'Untitled role'}
        </button>
      ))}
    </div>
  );
}

function RecentSearches({ collapsed, enabled = true }: { collapsed: boolean; enabled?: boolean }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const activeId = pathname === '/search' ? new URLSearchParams(search).get('id') : null;
  const [recent, setRecent] = useState<SearchDoc[]>([]);

  // Refetch when the active search changes — picks up newly-created searches without a hard reload.
  useEffect(() => {
    if (!enabled) {
      setRecent([]);
      return undefined;
    }

    let cancelled = false;
    listSavedSearches(8)
      .then((items) => {
        if (!cancelled) setRecent(items);
      })
      .catch(() => {
        if (!cancelled) setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeId, enabled]);

  if (recent.length === 0) return null;

  return (
    <div className={collapsed ? 'ats-recent-jobs collapsed-hidden' : 'ats-recent-jobs'}>
      <p>Recent searches</p>
      {recent.slice(0, 8).map((s) => {
        const isActive = activeId === s._id;
        return (
          <button
            key={s._id}
            type="button"
            title={s.prompt}
            data-active={isActive ? 'true' : undefined}
            style={
              isActive
                ? { color: 'var(--accent-strong)', fontWeight: 500, background: 'var(--accent-subtle)' }
                : undefined
            }
            onClick={() => navigate(`/search?id=${s._id}`)}
          >
            {s.title || 'Untitled search'}
          </button>
        );
      })}
    </div>
  );
}

function RecentSidebar({ collapsed, enabled = true }: { collapsed: boolean; enabled?: boolean }) {
  const { pathname } = useLocation();
  const onLegacySearch = pathname === '/search' || pathname.startsWith('/search/');
  const onAgentSearch =
    pathname === '/searches' ||
    pathname === '/searches-v2' ||
    pathname === '/searches-v3' ||
    pathname.startsWith('/search-v2/') ||
    pathname.startsWith('/search-v3/');

  if (onAgentSearch) return null;
  return onLegacySearch ? (
    <RecentSearches collapsed={collapsed} enabled={enabled} />
  ) : (
    <RecentJobs collapsed={collapsed} enabled={enabled} />
  );
}

function DevNavGroup({
  collapsed,
  entries,
  isActive,
}: {
  collapsed: boolean;
  entries: Array<{ item: NavItem; route: AppRoute }>;
  isActive: (item: NavItem) => boolean;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(window.localStorage.getItem(DEV_GROUP_KEY) === 'true');
  }, []);

  function toggle() {
    setOpen((value) => {
      const next = !value;
      window.localStorage.setItem(DEV_GROUP_KEY, String(next));
      return next;
    });
  }

  if (entries.length === 0) return null;

  return (
    <div className={collapsed ? 'ats-dev-group collapsed-hidden' : 'ats-dev-group'}>
      <button type="button" className="ats-dev-header" onClick={toggle}>
        <ChevronRight size={12} strokeWidth={2} className={open ? 'open' : ''} />
        <Cpu size={14} strokeWidth={1.8} />
        <span>Development</span>
      </button>
      <div className={open ? 'ats-dev-list open' : 'ats-dev-list'}>
        {entries.map(({ item, route }) => (
          <SidebarNavItem
            key={`${item.routeId}-${item.label}`}
            item={item}
            route={route}
            collapsed={false}
            active={isActive(item)}
          />
        ))}
        <DesignSystemGroup collapsed={false} />
      </div>
    </div>
  );
}

/**
 * Minimal nav rendered for the search-only org preset (see [[isSearchOnlyOrg]]).
 * The full app nav is hidden; users see only "New session" → / and
 * "Searches" → /searches-v3, plus Outreach when the org has that preset feature.
 * UserMenu stays so users can still sign out / switch workspaces.
 */
function SearchOnlySidebarBody() {
  const location = useLocation();
  const navigate = useNavigate();
  const { organization, permissions } = useAuth();
  const searchesActive =
    location.pathname === '/searches-v3' || location.pathname.startsWith('/search-v3/');
  const outreachActive =
    location.pathname === '/v2/inbox' ||
    location.pathname.startsWith('/v2/outreach') ||
    /^\/v2\/jobs\/[^/]+\/outreach/.test(location.pathname);
  const showOutreach = hasOutreachPreset(organization) && permissions.includes('outreach.view');
  const showTalentPool = hasCryptoLocalSearch(organization);
  const talentPoolActive = location.pathname.startsWith('/talent-pool');

  return (
    <>
      <nav className="ats-sidebar-nav">
        <div className="ats-nav-list">
          <div className="ats-nav-row">
            <button
              type="button"
              className="ats-nav-link"
              onClick={() => navigate('/')}
            >
              <span className="ats-nav-icon">
                <Plus size={16} strokeWidth={1.75} />
              </span>
              <span className="ats-nav-label">New session</span>
            </button>
          </div>
          <div className={searchesActive ? 'ats-nav-row active' : 'ats-nav-row'}>
            <button
              type="button"
              className="ats-nav-link"
              onClick={() => navigate('/searches-v3')}
            >
              <span className="ats-nav-icon">
                <Search size={16} strokeWidth={1.75} />
              </span>
              <span className="ats-nav-label">Searches</span>
            </button>
          </div>
          {showTalentPool && (
            <div className={talentPoolActive ? 'ats-nav-row active' : 'ats-nav-row'}>
              <button
                type="button"
                className="ats-nav-link"
                onClick={() => navigate('/talent-pool')}
              >
                <span className="ats-nav-icon">
                  <Users size={16} strokeWidth={1.75} />
                </span>
                <span className="ats-nav-label">Talent Pool</span>
              </button>
            </div>
          )}
          {showOutreach && (
            <div className={outreachActive ? 'ats-nav-row active' : 'ats-nav-row'}>
              <button
                type="button"
                className="ats-nav-link"
                onClick={() => navigate('/v2/inbox')}
              >
                <span className="ats-nav-icon">
                  <Send size={16} strokeWidth={1.75} />
                </span>
                <span className="ats-nav-label">Outreach</span>
              </button>
            </div>
          )}
        </div>
      </nav>

      <div className="ats-sidebar-bottom">
        <UserMenu collapsed={false} />
      </div>
    </>
  );
}

/**
 * Minimal nav for the "default settings" org — one with no temporary-preset
 * features (see [[isDefaultOrg]]). Only three destinations: New session (→ Home
 * composer), Jobs (→ /jobs), and Inbox (→ /inbox). The Sessions list is hidden
 * for now (the /sessions route still resolves). Everything else (Tasks,
 * Searches, Outreach, Notetakers, Brain, …) is intentionally hidden here; those
 * surfaces only appear for preset-flagged orgs, which keep the full nav below.
 */
function DefaultSidebarBody() {
  const location = useLocation();
  const navigate = useNavigate();
  const jobsActive =
    location.pathname === '/jobs' || /^\/v2\/jobs\/[^/]+\/manage/.test(location.pathname);
  const inboxActive = location.pathname === '/inbox';

  return (
    <>
      <nav className="ats-sidebar-nav">
        <div className="ats-nav-list">
          <div className="ats-nav-row">
            <button type="button" className="ats-nav-link" onClick={() => navigate('/')}>
              <span className="ats-nav-icon">
                <Plus size={16} strokeWidth={1.75} />
              </span>
              <span className="ats-nav-label">New session</span>
            </button>
          </div>
          <div className={jobsActive ? 'ats-nav-row active' : 'ats-nav-row'}>
            <button type="button" className="ats-nav-link" onClick={() => navigate('/jobs')}>
              <span className="ats-nav-icon">
                <BriefcaseBusiness size={16} strokeWidth={1.75} />
              </span>
              <span className="ats-nav-label">Jobs</span>
            </button>
          </div>
          <div className={inboxActive ? 'ats-nav-row active' : 'ats-nav-row'}>
            <button type="button" className="ats-nav-link" onClick={() => navigate('/inbox')}>
              <span className="ats-nav-icon">
                <Inbox size={16} strokeWidth={1.75} />
              </span>
              <span className="ats-nav-label">Inbox</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="ats-sidebar-bottom">
        <UserMenu collapsed={false} />
      </div>
    </>
  );
}

/**
 * Inner nav body shared by both the docked and floating sidebars. Kept as a
 * pure render of the menu so swapping wrappers (docked ↔ floating) doesn't
 * change the content — only the chrome around it.
 */
function SidebarBody({ active = true }: { active?: boolean }) {
  const location = useLocation();
  const { permissions, organization } = useAuth();
  // Needs-reply badge on the Outreach item. Polled only when the user can see
  // outreach; the shared poller is reused by the hub tab strip + campaign setup.
  const outreachCounts = useOutreachCounts(active && permissions.includes('outreach.view'));

  const visiblePrimaryNavItems = useMemo(
    () =>
      PRIMARY_NAV_ITEMS.map((item) => ({ item, route: getRouteById(item.routeId) }))
        .filter((entry): entry is { item: NavItem; route: AppRoute } => Boolean(entry.route))
        .filter(({ item }) => !item.requirePreset || item.requirePreset(organization))
        .filter(({ route }) => {
          if (!route.requireAnyPermission) return true;
          return route.requireAnyPermission.some((permission) => permissions.includes(permission));
        }),
    [permissions, organization],
  );

  const bottomNavItems = BOTTOM_NAV_ITEMS.map((item) => ({ item, route: getRouteById(item.routeId) })).filter(
    (entry): entry is { item: NavItem; route: AppRoute } => Boolean(entry.route),
  );

  if (isSearchOnlyOrg(organization)) {
    return <SearchOnlySidebarBody />;
  }

  // Default settings (no preset features) → the pared-back 3-item nav.
  if (isDefaultOrg(organization)) {
    return <DefaultSidebarBody />;
  }

  const isActive = (item: NavItem) =>
    (item.matchPaths ?? [item.matchPath]).some((path) => location.pathname.startsWith(path));

  return (
    <>
      <nav className="ats-sidebar-nav">
        <div className="ats-nav-list">
          {visiblePrimaryNavItems.map(({ item, route }) => (
            <SidebarNavItem
              key={`${item.routeId}-${item.label}`}
              item={item}
              route={route}
              collapsed={false}
              active={isActive(item)}
              badge={item.routeId === 'inbox' ? outreachCounts.needsAction : undefined}
            />
          ))}
        </div>
        <RecentSidebar collapsed={false} enabled={active} />
      </nav>

      <div className="ats-sidebar-bottom">
        <div className="ats-nav-list">
          {bottomNavItems.map(({ item, route }) => (
            <SidebarNavItem
              key={item.routeId}
              item={item}
              route={route}
              collapsed={false}
              active={isActive(item)}
            />
          ))}
        </div>
        <UserMenu collapsed={false} />
      </div>
    </>
  );
}

/**
 * Pinned sidebar. Stays mounted in both states so it can animate (translateX +
 * opacity) when collapsing/expanding — see `.ats-sidebar` / `.ats-shell-body
 * .sidebar-collapsed .ats-sidebar:not(.floating)` in app.css. When collapsed
 * it slides offscreen with `pointer-events: none`, so [[FloatingSidebar]] can
 * still own the peek interaction without competing for input.
 */
function DockedSidebar({
  onToggleCollapse,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  active,
}: {
  onToggleCollapse: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  onBack: () => void;
  onForward: () => void;
  active: boolean;
}) {
  return (
    <aside className="ats-sidebar">
      <div className="ats-sidebar-header">
        <button
          type="button"
          className="ats-brand-button"
          onClick={onToggleCollapse}
          aria-label="Hide sidebar"
        >
          <PanelToggleIcon />
        </button>
        <div className="ats-sidebar-history-controls" aria-label="Page history">
          <button type="button" aria-label="Back" title="Back" disabled={!canGoBack} onClick={onBack}>
            <ArrowLeft size={15} strokeWidth={1.7} />
          </button>
          <button type="button" aria-label="Forward" title="Forward" disabled={!canGoForward} onClick={onForward}>
            <ArrowRight size={15} strokeWidth={1.7} />
          </button>
        </div>
      </div>
      <SidebarBody active={active} />
    </aside>
  );
}

/**
 * Hover-peek sidebar shown when collapsed. Always mounted while collapsed so
 * the `.peeking` opacity transition can animate in/out as the cursor enters
 * and leaves the peek trigger; the inner header is omitted because the peek
 * trigger itself is the toggle in this mode.
 */
function FloatingSidebar({
  peeking,
  onPeekEnter,
  onPeekLeave,
  active,
}: {
  peeking: boolean;
  onPeekEnter: () => void;
  onPeekLeave: () => void;
  active: boolean;
}) {
  const className = peeking ? 'ats-sidebar floating peeking' : 'ats-sidebar floating';
  return (
    <aside className={className} onMouseEnter={onPeekEnter} onMouseLeave={onPeekLeave}>
      <SidebarBody active={active} />
    </aside>
  );
}

function SortableTab({
  tab,
  isActive,
  ignoreNextClickRef,
  navigateToTab,
  closeTab,
}: {
  tab: OpenTab;
  isActive: boolean;
  ignoreNextClickRef: MutableRefObject<boolean>;
  navigateToTab: (tab: OpenTab) => void;
  closeTab: (id: string) => void;
}) {
  const route = matchRoute(tab.pathname);
  const Icon = route?.icon;
  const {
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: tab.id });
  const className = ['ats-tab', isActive ? 'active' : '', isDragging ? 'dragging' : ''].filter(Boolean).join(' ');

  return (
    <div
      ref={setNodeRef}
      className={className}
      role="tab"
      aria-selected={isActive}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined,
      }}
      onClick={() => {
        if (ignoreNextClickRef.current) return;
        navigateToTab(tab);
      }}
      {...listeners}
    >
      <span className="ats-tab-main">
        {Icon ? (
          <span className="ats-tab-icon" aria-hidden="true">
            <Icon size={14} strokeWidth={1.8} />
          </span>
        ) : null}
        <span>{tab.title}</span>
      </span>
      <button
        type="button"
        className="ats-tab-close"
        aria-label={`Close ${tab.title}`}
        onClick={(event) => {
          event.stopPropagation();
          closeTab(tab.id);
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}

function TopTabBar({
  tabs,
  activeTabId,
  multiTabEnabled,
  navigateToTab,
  openTab,
  closeTab,
  reorderTabs,
  onCollapse,
}: {
  tabs: OpenTab[];
  activeTabId: string;
  multiTabEnabled: boolean;
  navigateToTab: (tab: OpenTab) => void;
  openTab: (pathname?: string, search?: string) => void;
  closeTab: (id: string) => void;
  reorderTabs: (activeId: string, overId: string) => void;
  onCollapse: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const platform = window.lanternDesktop?.platform;
  const isDesktop = window.lanternDesktop?.isDesktop;
  const ignoreNextTabClickRef = useRef(false);
  const tabClickGuardTimerRef = useRef<number | null>(null);
  const [windowExpanded, setWindowExpanded] = useState(false);
  const [collapseHover, setCollapseHover] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const currentRoute = matchRoute(location.pathname);
  const CurrentRouteIcon = currentRoute?.icon;
  const currentPageTitle =
    location.pathname === '/' ? 'Home' : currentRoute?.tabLabel || currentRoute?.title || 'Lantern ATS';
  const visibleTabs = tabs.filter((tab) => tab.pathname !== '/');
  const visibleTabIds = visibleTabs.map((tab) => tab.id);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 7 } }));

  function releaseTabClickGuard() {
    if (tabClickGuardTimerRef.current !== null) {
      window.clearTimeout(tabClickGuardTimerRef.current);
    }

    tabClickGuardTimerRef.current = window.setTimeout(() => {
      ignoreNextTabClickRef.current = false;
      tabClickGuardTimerRef.current = null;
    }, 0);
  }

  function handleTabDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      reorderTabs(String(active.id), String(over.id));
    }

    releaseTabClickGuard();
  }

  useEffect(() => {
    if (!window.lanternDesktop) return undefined;

    let cancelled = false;
    const applyState = (state: LanternDesktopWindowState) => {
      if (!cancelled) {
        setWindowExpanded(state.isFullScreen || state.isMaximized);
      }
    };

    void window.lanternDesktop.getWindowState().then(applyState);
    const unsubscribe = window.lanternDesktop.onWindowStateChange(applyState);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (tabClickGuardTimerRef.current !== null) {
        window.clearTimeout(tabClickGuardTimerRef.current);
      }
    };
  }, []);

  const className = [
    'ats-top-tabbar',
    isDesktop ? 'desktop' : 'browser',
    platform === 'darwin' ? 'darwin' : '',
    windowExpanded ? 'expanded' : '',
    multiTabEnabled ? '' : 'single-page',
  ]
    .filter(Boolean)
    .join(' ');

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const center = rect.width / 2;
    // Show toggle when within ~140px of horizontal center.
    setCollapseHover(Math.abs(x - center) < 140);
  }

  return (
    <div
      ref={barRef}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setCollapseHover(false)}
    >
      <div className="ats-window-drag-spacer" aria-hidden="true" />
      <button
        type="button"
        className={location.pathname === '/' ? 'ats-home-button active' : 'ats-home-button'}
        aria-label="Home"
        title="Home"
        onClick={() => navigate('/')}
      >
        <Home size={17} strokeWidth={1.7} />
      </button>
      {multiTabEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => {
            ignoreNextTabClickRef.current = true;
          }}
          onDragCancel={releaseTabClickGuard}
          onDragEnd={handleTabDragEnd}
        >
          <SortableContext items={visibleTabIds} strategy={horizontalListSortingStrategy}>
            <div className="ats-tabs-scroll" role="tablist" aria-label="Open pages">
              {visibleTabs.map((tab) => (
                <SortableTab
                  key={tab.id}
                  tab={tab}
                  isActive={location.pathname !== '/' && tab.id === activeTabId}
                  ignoreNextClickRef={ignoreNextTabClickRef}
                  navigateToTab={navigateToTab}
                  closeTab={closeTab}
                />
              ))}
              <button type="button" className="ats-new-tab-button" title="New tab" onClick={() => openTab()}>
                <Plus size={15} />
              </button>
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="ats-single-page-title" aria-live="polite">
          {CurrentRouteIcon ? (
            <span className="ats-tab-icon" aria-hidden="true">
              <CurrentRouteIcon size={14} strokeWidth={1.8} />
            </span>
          ) : null}
          <span>{currentPageTitle}</span>
        </div>
      )}
      <div className="ats-tabbar-toggle-hotzone" aria-hidden="true" />
      <button
        type="button"
        className={collapseHover ? 'ats-tabbar-toggle visible' : 'ats-tabbar-toggle'}
        aria-label="Hide tab bar"
        title="Hide tab bar"
        onClick={collapseHover ? onCollapse : undefined}
      >
        <ChevronUp size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

function TabBarRevealZone({ onExpand }: { onExpand: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="ats-tabbar-reveal-zone"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="ats-tabbar-toggle-hotzone" aria-hidden="true" />
      <button
        type="button"
        className={hover ? 'ats-tabbar-toggle visible' : 'ats-tabbar-toggle'}
        aria-label="Show tab bar"
        title="Show tab bar"
        onClick={hover ? onExpand : undefined}
      >
        <ChevronDown size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

function FullscreenTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const route = matchRoute(location.pathname);
  const title = route?.title || 'Lantern ATS';

  function handleClose() {
    // Always exit fullscreen in one click — no matter how many Settings/Brain
    // pages the user has visited. Falls back to home if no normal page has
    // been seen this session (e.g. deep-linked to /settings).
    const target = lastNonFullscreenLocation && !isFullscreenPath(lastNonFullscreenLocation.split('?')[0])
      ? lastNonFullscreenLocation
      : '/';
    navigate(target);
  }

  return (
    <div className="ats-fullscreen-topbar">
      <button type="button" className="ats-fullscreen-back" onClick={handleClose} aria-label="Back">
        <ArrowLeft size={15} strokeWidth={1.75} />
        <span>Back</span>
      </button>
      <div className="ats-fullscreen-title">{title}</div>
      <UserMenu variant="topbar" />
    </div>
  );
}

/**
 * Renders every open tab as an <Activity> wrapper plus a virtual home tab.
 * Active tab → mode="visible". Hidden tabs stay mounted with effects paused
 * (React 19.2 contract), so switching tabs is instant — no remount, no lost
 * Lexical/React-Flow state, no refetches.
 *
 * Each tab gets its own <Routes location={tab.url}> so children using
 * useLocation/useParams/useSearchParams see *their own* URL, not the active
 * tab's. Without this, hidden tabs misread params and break in subtle ways.
 *
 * Routes can opt out via routeMeta.keepAlive === false — those render only
 * when active and remount on every reactivation (today's behavior).
 */
function KeepAliveRouteHost({
  tabs,
  activeTabId,
  atHome,
  liveLocation,
}: {
  tabs: OpenTab[];
  activeTabId: string;
  atHome: boolean;
  liveLocation: ReturnType<typeof useLocation>;
}) {
  // For the active tab, pass the *live* router location so children see the
  // real `state` from `navigate({ state: ... })` (e.g. WorkspaceHome → ChatDetail
  // initial-message handoff). For inactive tabs, synthesize a path-only location
  // so each tab's children read their own URL without seeing another tab's state.
  const renderRoutes = (loc: string | ReturnType<typeof useLocation>) => (
    <Routes location={loc}>
      {appRoutes.map((route) => (
        <Route key={route.id} path={route.path} element={route.element} />
      ))}
      <Route path="/v2" element={<Navigate to="/v2/jobs" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );

  return (
    <>
      {/* Virtual home tab — kept alive so returning to / is instant. */}
      <Activity mode={atHome ? 'visible' : 'hidden'} name="home">
        <TabContext.Provider value={{ tabId: HOME_TAB_ID, isActive: atHome }}>
          {renderRoutes(atHome ? liveLocation : HOME_PATH)}
        </TabContext.Provider>
      </Activity>
      {tabs.map((tab) => {
        const isActive = !atHome && tab.id === activeTabId;
        const route = matchRoute(tab.pathname);
        // keepAlive: false routes only render when active and remount on switch.
        if (route?.keepAlive === false && !isActive) return null;
        return (
          <Activity key={tab.id} mode={isActive ? 'visible' : 'hidden'} name={tab.title}>
            <TabContext.Provider value={{ tabId: tab.id, isActive }}>
              {renderRoutes(isActive ? liveLocation : `${tab.pathname}${tab.search}`)}
            </TabContext.Provider>
          </Activity>
        );
      })}
    </>
  );
}

export function AppShell() {
  const [commandOpen, setCommandOpen] = useState(false);
  const [multiTabEnabled, setMultiTabEnabled] = useState(readMultiTabEnabled);
  const [tabBarCollapsed, setTabBarCollapsed] = useState(readTabBarCollapsed);
  // No setter — keep-alive is a localStorage kill switch, not a runtime UI toggle.
  // Reload required to flip it (intentional: simpler reasoning, no half-mounted state).
  const [keepAliveEnabled] = useState(readKeepAliveEnabled);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.localStorage.getItem(COLLAPSE_KEY) === 'true',
  );
  const [sidebarPeeking, setSidebarPeeking] = useState(false);
  const peekCloseTimerRef = useRef<number | null>(null);
  const peekSuppressedRef = useRef(false);
  const peekSuppressTimerRef = useRef<number | null>(null);
  const tabs = useTabs();
  const location = useLocation();
  const fullscreen = isFullscreenPath(location.pathname);

  useEffect(() => {
    return () => {
      if (peekCloseTimerRef.current !== null) {
        window.clearTimeout(peekCloseTimerRef.current);
      }
      if (peekSuppressTimerRef.current !== null) {
        window.clearTimeout(peekSuppressTimerRef.current);
      }
    };
  }, []);

  // Sync AppShell's local sidebar state when other surfaces (e.g.
  // the agent chat header's expand button) write the shared
  // localStorage key + dispatch the change event. Without this, an
  // external toggle would update localStorage but AppShell wouldn't
  // re-render its sidebar.
  //
  // The handler GUARDS against feedback by re-reading localStorage
  // and only calling setState when the React state disagrees. That
  // way when AppShell's own toggle dispatches the event (so external
  // listeners pick it up), the handler is a no-op for AppShell.
  useEffect(() => {
    const handler = () => {
      const next = window.localStorage.getItem(COLLAPSE_KEY) === 'true';
      setSidebarCollapsed((prev) => (prev === next ? prev : next));
      if (!next) setSidebarPeeking(false);
    };
    window.addEventListener('lantern-sidebar-collapsed-change', handler);
    return () =>
      window.removeEventListener('lantern-sidebar-collapsed-change', handler);
  }, []);

  function toggleSidebarCollapse() {
    // Side effects (localStorage write, custom event dispatch, timer
    // setup) live OUTSIDE the setState updater so they don't run
    // mid-update. Earlier this dispatched the event INSIDE the
    // updater callback, which fired the listener synchronously
    // during React's state commit — a re-entrant setState pattern
    // that React doesn't reliably support, with the visible
    // symptom that AppShell's own collapse button stopped working
    // after the agent's expand button had been used.
    const next = !sidebarCollapsed;
    window.localStorage.setItem(COLLAPSE_KEY, String(next));
    if (next) {
      // Prevent auto-peek when the trigger mounts under a stationary cursor.
      peekSuppressedRef.current = true;
      if (peekSuppressTimerRef.current !== null) {
        window.clearTimeout(peekSuppressTimerRef.current);
      }
      peekSuppressTimerRef.current = window.setTimeout(() => {
        peekSuppressedRef.current = false;
        peekSuppressTimerRef.current = null;
      }, 400);
    } else {
      setSidebarPeeking(false);
    }
    setSidebarCollapsed(next);
    // Notify external subscribers (e.g. agent chat header's
    // expand button) so they re-render in sync. The dispatch happens
    // AFTER setSidebarCollapsed so the listener doesn't fire during
    // our own state update.
    window.dispatchEvent(new CustomEvent('lantern-sidebar-collapsed-change'));
  }

  function openSidebarPeek() {
    if (peekSuppressedRef.current) return;
    if (peekCloseTimerRef.current !== null) {
      window.clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }
    setSidebarPeeking(true);
  }

  // Subscribe to peek requests dispatched by other surfaces (e.g. the
  // agent chat header's in-flow expand icon, which sits outside our
  // global peek trigger). Routes them through the same debounce/suppress
  // logic the global trigger uses so behavior stays consistent.
  useEffect(
    () => onSidebarPeekRequest(openSidebarPeek, scheduleSidebarPeekClose),
    [],
  );

  function scheduleSidebarPeekClose() {
    // A genuine mouse-leave releases suppression early — re-entry is then intentional.
    if (peekSuppressedRef.current) {
      peekSuppressedRef.current = false;
      if (peekSuppressTimerRef.current !== null) {
        window.clearTimeout(peekSuppressTimerRef.current);
        peekSuppressTimerRef.current = null;
      }
    }
    if (peekCloseTimerRef.current !== null) {
      window.clearTimeout(peekCloseTimerRef.current);
    }
    peekCloseTimerRef.current = window.setTimeout(() => {
      setSidebarPeeking(false);
      peekCloseTimerRef.current = null;
    }, 180);
  }

  // FullPager is a single shell-level overlay shared across all tabs; its open
  // state must follow the *active* tab's URL. With per-tab routing, switching
  // tabs changes window.location to the new tab's URL — sync the store from there.
  useEffect(() => {
    useFullPagerStore.getState().syncFromUrl();
  }, [location.pathname, location.search]);

  function toggleTabBar() {
    setTabBarCollapsed((prev) => {
      const next = !prev;
      writeTabBarCollapsed(next);
      return next;
    });
  }

  useEffect(() => {
    if (!isFullscreenPath(location.pathname)) {
      lastNonFullscreenLocation = `${location.pathname}${location.search || ''}`;
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isCommandShortcut = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();

      if (isCommandShortcut && event.shiftKey && key === 'm') {
        event.preventDefault();
        if (event.repeat) return;

        setMultiTabEnabled((enabled) => {
          const next = !enabled;
          writeMultiTabEnabled(next);
          return next;
        });
        return;
      }

      if (isCommandShortcut && key === 'k') {
        event.preventDefault();
        setCommandOpen(true);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (fullscreen) {
    return (
      <div className="app-frame ats-app-frame ats-fullscreen-frame">
        <FullscreenTopBar />
        <main className="ats-fullscreen-main">
          <Outlet />
        </main>
        <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
        <FullPagerPortal />
      </div>
    );
  }

  return (
    <div className="app-frame ats-app-frame">
      <div
        className={`ats-shell-body ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
        /*
          Per-route hook for CSS overrides. Used by app.css to hide the
          global .ats-sidebar-peek-trigger on routes that render their own
          in-flow expand icon (components/SidebarExpandButton) in their
          page header — letting the global trigger also render would
          visually collide with that header content. Routes without an
          in-flow icon (e.g. /searches, /search/new) keep the global
          trigger.
        */
        data-route={
          routeRendersOwnSidebarToggle(location.pathname)
            ? 'self-toggle'
            : undefined
        }
      >
        {sidebarCollapsed && (
          <>
            <button
              type="button"
              className="ats-sidebar-peek-trigger"
              aria-label="Show sidebar"
              title="Show sidebar"
              onClick={toggleSidebarCollapse}
              onMouseEnter={openSidebarPeek}
              onMouseLeave={scheduleSidebarPeekClose}
            >
              <PanelToggleIcon />
            </button>
            <FloatingSidebar
              peeking={sidebarPeeking}
              onPeekEnter={openSidebarPeek}
              onPeekLeave={scheduleSidebarPeekClose}
              active={sidebarPeeking}
            />
          </>
        )}
        {/*
          DockedSidebar stays mounted in both states so it can animate
          (translateX + opacity) when toggling. CSS hides it offscreen
          when .ats-shell-body has .sidebar-collapsed.
        */}
        <DockedSidebar
          onToggleCollapse={toggleSidebarCollapse}
          canGoBack={tabs.canGoBackInTab}
          canGoForward={tabs.canGoForwardInTab}
          onBack={tabs.goBackInTab}
          onForward={tabs.goForwardInTab}
          active={!sidebarCollapsed}
        />
        <main className="ats-work-surface">
          <div className="ats-route-viewport">
            {keepAliveEnabled ? (
              <KeepAliveRouteHost
                tabs={tabs.tabs}
                activeTabId={tabs.activeTabId}
                atHome={location.pathname === HOME_PATH}
                liveLocation={location}
              />
            ) : (
              <Outlet />
            )}
          </div>
        </main>
      </div>
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      {/* Full-page application/person detail overlay. Mounted here (not in
       * a per-route layout) so it persists across route changes — opening
       * a candidate from the kanban shouldn't unmount the underlying
       * route, and the URL ?fp=… params are how it auto-opens on reload. */}
      <FullPagerPortal />
    </div>
  );
}
